import React from "react";
import { Fab } from "@mui/material";
import "./FloatButtons.scss";
import LoginIcon from "@mui/icons-material/Login";
import ListIcon from "@mui/icons-material/List";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import AccessibilityOutlinedIcon from "@mui/icons-material/AccessibilityOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";

import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import FeedOutlinedIcon from "@mui/icons-material/FeedOutlined";
import { DialogOpenType, openDialog } from "state/slices/DialogSlice";
import { useTypedDispatch } from "hooks/store";
import { GraphicService } from "Classes/GraphicService";

import { gsap, Expo } from "gsap";

enum ActionType {
    Logout = "logout",
    List = "list",
    Save = "save",
    SaveSceneJson = "saveSceneJson",
    Reload = "reload",
    NewSetup = "newSetup",
    LoadSetup = "loadSetup",
    TopView = "topView",
    ReportIssue = "reportIssue",
    ChangePatient = "changePatient",
    Import = "import",
}

const actions = [
    {
        icon: (
            <div className="btnSpeedDialAction">
                <CloudUploadOutlinedIcon className="icon" />{" "}
                <span className="label">Import scene</span>
            </div>
        ),
        name: "Import scene",
        type: ActionType.Import,
    },
    {
        icon: (
            <div className="btnSpeedDialAction">
                <FeedOutlinedIcon className="icon" /> <span className="label">Load setup</span>
            </div>
        ),
        name: "Load setup",
        type: ActionType.LoadSetup,
    },
    {
        icon: (
            <div className="btnSpeedDialAction">
                <AddCircleOutlineOutlinedIcon className="icon" />
                <span className="label">New Setup</span>
            </div>
        ),
        name: "New setup",
        type: ActionType.NewSetup,
    },
];

const FloatButtons = (): JSX.Element => {
    const dispatch = useTypedDispatch();
    const handleFabClick = (type: string): void => {
        switch (type) {
            case ActionType.Logout:
                dispatch(openDialog({ isDialogOpen: true, type: DialogOpenType.Logout }));
                break;
            case ActionType.List:
                break;
            case ActionType.Save:
                dispatch(openDialog({ isDialogOpen: true, type: DialogOpenType.SaveRoom }));
                break;
            case ActionType.Reload:
                dispatch(openDialog({ isDialogOpen: true, type: DialogOpenType.ResetRoom }));
                break;
            case ActionType.NewSetup:
                dispatch(openDialog({ isDialogOpen: true, type: DialogOpenType.NewSetup }));
                break;
            case ActionType.LoadSetup:
                dispatch(openDialog({ isDialogOpen: true, type: DialogOpenType.LoadSetup }));
                break;
            case ActionType.Import:
                dispatch(openDialog({ isDialogOpen: true, type: DialogOpenType.Import }));
                break;
            case ActionType.TopView:
                cameraToTopView();
                break;
            case ActionType.ReportIssue:
                dispatch(openDialog({ isDialogOpen: true, type: DialogOpenType.ReportIssue }));
                break;
            case ActionType.ChangePatient:
                dispatch(openDialog({ isDialogOpen: true, type: DialogOpenType.ChangePatient }));
                break;
            case ActionType.SaveSceneJson:
                dispatch(
                    openDialog({
                        isDialogOpen: true,
                        type: DialogOpenType.SaveSceneJson,
                    })
                );
                break;
        }
    };

    const cameraToTopView = () => {
        // Seconds
        const duration = 2;
        const timeline = gsap.timeline({ defaults: { duration, ease: Expo.easeInOut } });

        timeline
            .to(
                GraphicService.camera.position,
                {
                    x: 0,
                    y: 4,
                    z: 0,
                },
                0
            )
            .to(
                GraphicService.camera.rotation,
                {
                    x: -Math.PI / 2,
                    y: 0,
                    z: 0,
                },
                "<"
            );
    };
    return (
        <section className="floatButtonsWrapper">
            <div className="floatButtonContainer">
                <div className="inner">
                    <Tooltip title="Logout" arrow>
                        <Fab
                            aria-label="add"
                            size="medium"
                            onClick={() => handleFabClick(ActionType.Logout)}
                            className="fabButton"
                        >
                            <LoginIcon className="icon iconLogout" />
                        </Fab>
                    </Tooltip>

                    <div className="speedDialWrapper">
                        <Box
                            className="speedDialContainer"
                            sx={{
                                height: 200,
                            }}
                        >
                            <SpeedDial
                                id="loadOrNewSetup"
                                className="speedDial"
                                ariaLabel="CMRNewOrLoad"
                                icon={
                                    <span style={{ marginTop: 5 }}>
                                        <ListIcon className="icon" />
                                    </span>
                                }
                            >
                                {actions.map((action) => (
                                    <SpeedDialAction
                                        onClick={() => handleFabClick(action.type)}
                                        role="menuitem"
                                        key={action.name}
                                        title={action.name}
                                        icon={action.icon}
                                    />
                                ))}
                            </SpeedDial>
                        </Box>
                    </div>

                    <Tooltip title="Screenshot" arrow>
                        <Fab
                            onClick={() => handleFabClick(ActionType.Save)}
                            aria-label="Photo camera"
                            size="medium"
                            className="fabButton"
                        >
                            <PhotoCameraOutlinedIcon className="icon iconPhotoCamera" />
                        </Fab>
                    </Tooltip>

                    <Tooltip title="Save" arrow>
                        <Fab
                            onClick={() => handleFabClick(ActionType.SaveSceneJson)}
                            aria-label="save scene"
                            size="medium"
                            className="fabButton"
                        >
                            <SaveOutlinedIcon className="icon iconSave" />
                        </Fab>
                    </Tooltip>

                    <Tooltip title="Reset OR" arrow>
                        <Fab
                            onClick={() => handleFabClick(ActionType.Reload)}
                            aria-label="add"
                            size="medium"
                            className="fabButton"
                        >
                            <CachedOutlinedIcon className="icon iconReload" />
                        </Fab>
                    </Tooltip>
                    <Tooltip title="Centre View" arrow>
                        <Fab
                            onClick={() => handleFabClick(ActionType.TopView)}
                            aria-label="Sky View"
                            size="medium"
                            className="fabButton"
                        >
                            <VisibilityIcon className="icon" />
                        </Fab>
                    </Tooltip>

                    <Tooltip title="Change Patient" arrow>
                        <Fab
                            onClick={() => handleFabClick(ActionType.ChangePatient)}
                            aria-label="Change Patient"
                            size="medium"
                            className="fabButton"
                        >
                            <AccessibilityOutlinedIcon className="icon" />
                        </Fab>
                    </Tooltip>
                </div>
                <Fab
                    onClick={() => handleFabClick(ActionType.ReportIssue)}
                    aria-label="Help Text"
                    size="medium"
                    className="fabButton help"
                >
                    <span className="text">Help</span>
                </Fab>
            </div>
        </section>
    );
};

export default FloatButtons;
